/**
 * The seven empty states (spec #44 §3), in one component so none of them
 * can quietly become a blank pane.
 *
 * A FRESH TENANT MUST BE LEGIBLE. Every state here says what is true and,
 * where there is one, what to do next. What none of them does is fabricate:
 * no invented cadence, no placeholder metric, and no zero presented as if
 * it were a measurement.
 */

import { Button, Text } from '@shopify/polaris';
import { Link } from 'react-router-dom';

import type { WorkspaceEmptyKind } from '../../types/workspace';
import styles from './workspace.module.css';

export interface WorkspaceEmptyProps {
  kind: WorkspaceEmptyKind;
  /** `no-plan`: opens the draft proposal waiting in the inbox, if any. */
  draftProposalId?: string | null;
  /** `no-cycle`: the NEXT SCHEDULED cadence, verbatim from the server.
   * Absent means we do not know it — and then we do not claim one. */
  nextCadence?: string | null;
  /** `ask-not-entitled` / `lane-error`: the server's own reason. */
  reason?: string | null;
}

export function WorkspaceEmpty({
  kind,
  draftProposalId,
  nextCadence,
  reason,
}: WorkspaceEmptyProps) {
  return (
    <div className={styles.empty} data-testid={`empty-${kind}`}>
      {body()}
    </div>
  );

  function title(text: string) {
    return (
      <div className={styles.emptyTitle}>
        <Text as="h3" variant="headingSm">
          {text}
        </Text>
      </div>
    );
  }

  function body() {
    switch (kind) {
      case 'no-plan':
        return (
          <>
            {title('No plan yet')}
            <Text as="p" tone="subdued">
              Nothing is committed, so there is nothing to measure yet.
            </Text>
            {draftProposalId ? (
              <div style={{ marginTop: 8 }}>
                <Button
                  url={`/approvals?tab=pending&highlight=${draftProposalId}`}
                >
                  Review the draft plan
                </Button>
              </div>
            ) : null}
          </>
        );
      case 'no-cycle':
        return (
          <>
            {title('No cycle has run yet')}
            <Text as="p" tone="subdued">
              {nextCadence
                ? `The next one is scheduled ${nextCadence}.`
                : 'Nothing is scheduled that this screen can see yet.'}
            </Text>
          </>
        );
      case 'no-questions':
        return (
          <>
            {title('Nothing needs you')}
            <Text as="p" tone="subdued">
              Every open question has been answered. New ones land here.
            </Text>
          </>
        );
      case 'no-agents-configured':
        return (
          <>
            {title('No agents are set up yet')}
            <Text as="p" tone="subdued">
              Set one up and it appears here with its own channel.
            </Text>
            <div style={{ marginTop: 8 }}>
              <Button url="/agents">Set up an agent</Button>
            </div>
          </>
        );
      case 'ask-not-entitled':
        return (
          <>
            {title('Asking is not on your plan')}
            <Text as="p" tone="subdued">
              {reason ?? 'This workspace cannot ask questions on this plan.'}
            </Text>
            <div style={{ marginTop: 8 }}>
              <Button url="/billing">See plans</Button>
            </div>
          </>
        );
      case 'no-specialists':
        return (
          <>
            {title('No specialists enabled')}
            <Text as="p" tone="subdued">
              <Link to="/marketing-agents/specialists">
                Browse the catalog
              </Link>{' '}
              and enable the ones this tenant needs.
            </Text>
          </>
        );
      case 'lane-error':
      default:
        return (
          <>
            {title('This lane could not load')}
            <Text as="p" tone="subdued">
              {reason ?? 'Try again in a moment.'}
            </Text>
          </>
        );
    }
  }
}
